// frontend/src/components/landing/sections/LandingHeader.js
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ButtonBase, Collapse, Link } from '@mui/material';
import { Container, LandingButton } from '../parts';
import { CloseIcon, MenuIcon } from '../icons';
import { tokens } from '../landingTheme';

export const NAV_LINKS = [
    { label: 'How it works', href: '#how-it-works' },
    { label: 'What it measures', href: '#what-it-measures' },
    { label: 'Research', href: '#research' },
];

const linkSx = {
    color: tokens.ink,
    fontSize: '0.9375rem',
    fontWeight: 500,
    textDecoration: 'none',
    borderRadius: 1,
    transition: 'color 200ms ease-out',
    '&:hover': { color: tokens.accentMid },
    '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 4 },
};

export const Logo = () => (
    <Box
        component={RouterLink}
        to="/"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.25,
            color: tokens.ink,
            textDecoration: 'none',
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 4 },
        }}
    >
        <Box
            aria-hidden="true"
            sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: tokens.accent,
                color: '#FFFFFF',
                display: 'grid',
                placeItems: 'center',
                fontFamily: tokens.display,
                fontSize: '1.125rem',
                fontWeight: 600,
            }}
        >
            A
        </Box>
        <Box component="span" sx={{ fontWeight: 600, fontSize: '1.0625rem', letterSpacing: '-0.01em' }}>
            ADAS
        </Box>
    </Box>
);

const LandingHeader = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const progressRef = useRef(null);

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 8);
            // Set directly on the element so scrolling does not re-render the header
            const max = document.documentElement.scrollHeight - window.innerHeight;
            if (progressRef.current) progressRef.current.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const closeMenu = () => setMenuOpen(false);

    return (
        <Box
            component="header"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: scrolled || menuOpen ? 'rgba(255, 255, 255, 0.92)' : 'transparent',
                borderBottom: `1px solid ${scrolled || menuOpen ? tokens.line : 'transparent'}`,
                transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
            }}
        >
            <Box
                ref={progressRef}
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: -1,
                    width: '100%',
                    height: 2,
                    background: `linear-gradient(90deg, ${tokens.accent}, ${tokens.highlight})`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                }}
            />
            <Container sx={{ display: 'flex', alignItems: 'center', minHeight: 72, gap: 4 }}>
                <Logo />

                <Box
                    component="nav"
                    aria-label="Landing page"
                    sx={{ display: { xs: 'none', md: 'flex' }, gap: 4, mx: 'auto' }}
                >
                    {NAV_LINKS.map((link) => (
                        <Link key={link.href} href={link.href} sx={linkSx}>
                            {link.label}
                        </Link>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 3 }, ml: { xs: 'auto', md: 0 } }}>
                    <Link component={RouterLink} to="/login" sx={{ ...linkSx, display: { xs: 'none', sm: 'inline' } }}>
                        Sign in
                    </Link>
                    <LandingButton to="/register" size="small">
                        Analyze a lesson
                    </LandingButton>
                    <ButtonBase
                        onClick={() => setMenuOpen((open) => !open)}
                        aria-expanded={menuOpen}
                        aria-controls="landing-mobile-menu"
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        sx={{
                            display: { xs: 'inline-flex', md: 'none' },
                            width: 40,
                            height: 40,
                            borderRadius: 999,
                            color: tokens.ink,
                            '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
                        }}
                    >
                        {menuOpen ? <CloseIcon /> : <MenuIcon />}
                    </ButtonBase>
                </Box>
            </Container>

            <Collapse in={menuOpen} sx={{ display: { md: 'none' } }}>
                <Container
                    id="landing-mobile-menu"
                    component="nav"
                    aria-label="Landing page"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1, pb: 3 }}
                >
                    {NAV_LINKS.map((link) => (
                        <Link key={link.href} href={link.href} onClick={closeMenu} sx={{ ...linkSx, fontSize: '1.125rem' }}>
                            {link.label}
                        </Link>
                    ))}
                    <Link component={RouterLink} to="/login" sx={{ ...linkSx, fontSize: '1.125rem' }}>
                        Sign in
                    </Link>
                </Container>
            </Collapse>
        </Box>
    );
};

export default LandingHeader;
